import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/admin/AdminLayout';
import { Microscope, Plus, Trash2, Save, AlertCircle, MoveUp, MoveDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function ResearchManagement() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('funded');
  const [fundedData, setFundedData] = useState(null);
  const [collabData, setCollabData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('cish_token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const [fundedRes, collabRes] = await Promise.all([
        axios.get(`${API_URL}/api/research/funded`),
        axios.get(`${API_URL}/api/research/collaboration`)
      ]);
      setFundedData(fundedRes.data);
      setCollabData(collabRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 404) {
        // Initialize with empty data
        setFundedData({
          id: 'research-funded-1',
          page_title: 'Research — Funded Projects',
          intro_text: '',
          default_tab: 'inhouse',
          inhouse_projects: [],
          externally_aided_projects: []
        });
        setCollabData({
          id: 'collaboration-1',
          page_title: 'Collaborations & Partnerships',
          about_text: '',
          collaborations: []
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSaveFunded = async () => {
    try {
      setSaving(true);
      setError('');
      await axios.post(`${API_URL}/api/research/funded`, fundedData, {
        headers: getAuthHeaders()
      });
      alert('Research Funded page updated successfully!');
    } catch (error) {
      console.error('Error saving:', error);
      setError(error.response?.data?.detail || 'Failed to save data');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveCollab = async () => {
    try {
      setSaving(true);
      setError('');
      await axios.post(`${API_URL}/api/research/collaboration`, collabData, {
        headers: getAuthHeaders()
      });
      alert('Collaboration page updated successfully!');
    } catch (error) {
      console.error('Error saving:', error);
      setError(error.response?.data?.detail || 'Failed to save data');
    } finally {
      setSaving(false);
    }
  };

  // In-House Projects handlers
  const addInHouseProject = () => {
    setFundedData({
      ...fundedData,
      inhouse_projects: [
        ...fundedData.inhouse_projects,
        {
          s_no: fundedData.inhouse_projects.length + 1,
          activity_title: '',
          principal_investigator: '',
          division: '',
          co_principal_investigator: null
        }
      ]
    });
  };

  const updateInHouseProject = (index, field, value) => {
    const updated = [...fundedData.inhouse_projects];
    updated[index][field] = value;
    setFundedData({ ...fundedData, inhouse_projects: updated });
  };

  const deleteInHouseProject = (index) => {
    const updated = fundedData.inhouse_projects.filter((_, i) => i !== index);
    // Renumber s_no
    updated.forEach((row, i) => row.s_no = i + 1);
    setFundedData({ ...fundedData, inhouse_projects: updated });
  };

  // Externally Aided Projects handlers
  const addExternalProject = () => {
    setFundedData({
      ...fundedData,
      externally_aided_projects: [
        ...fundedData.externally_aided_projects,
        {
          s_no: fundedData.externally_aided_projects.length + 1,
          scheme: '',
          project_title: '',
          pi_or_nodal_officer: '',
          period: ''
        }
      ]
    });
  };

  const updateExternalProject = (index, field, value) => {
    const updated = [...fundedData.externally_aided_projects];
    updated[index][field] = value;
    setFundedData({ ...fundedData, externally_aided_projects: updated });
  };

  const deleteExternalProject = (index) => {
    const updated = fundedData.externally_aided_projects.filter((_, i) => i !== index);
    updated.forEach((row, i) => row.s_no = i + 1);
    setFundedData({ ...fundedData, externally_aided_projects: updated });
  };

  // Collaboration handlers
  const addCollaboration = () => {
    setCollabData({
      ...collabData,
      collaborations: [
        ...collabData.collaborations,
        { title: '', description: '' }
      ]
    });
  };

  const updateCollaboration = (index, field, value) => {
    const updated = [...collabData.collaborations];
    updated[index][field] = value;
    setCollabData({ ...collabData, collaborations: updated });
  };

  const deleteCollaboration = (index) => {
    setCollabData({
      ...collabData,
      collaborations: collabData.collaborations.filter((_, i) => i !== index)
    });
  };

  const moveCollaboration = (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= collabData.collaborations.length) return;
    
    const updated = [...collabData.collaborations];
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    setCollabData({ ...collabData, collaborations: updated });
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-screen">
          <div className="text-lg">Loading...</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6 max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
              <Microscope className="w-8 h-8 text-[#0E6D2B]" />
              Research Management
            </h1>
            <p className="text-gray-600 mt-1">Manage research funded and collaboration pages</p>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-red-800">{error}</p>
          </div>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-2 w-full max-w-md mb-6">
            <TabsTrigger value="funded">Research Funded</TabsTrigger>
            <TabsTrigger value="collaboration">Collaboration</TabsTrigger>
          </TabsList>

          {/* Research Funded Tab */}
          <TabsContent value="funded">
            {fundedData && (
              <div className="space-y-6">
                {/* Header Fields */}
                <div className="bg-white rounded-lg shadow-sm border p-6">
                  <h2 className="text-xl font-bold mb-4">Page Settings</h2>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Page Title</label>
                      <Input
                        value={fundedData.page_title}
                        onChange={(e) => setFundedData({ ...fundedData, page_title: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Intro Text (HTML)</label>
                      <Textarea
                        value={fundedData.intro_text}
                        onChange={(e) => setFundedData({ ...fundedData, intro_text: e.target.value })}
                        rows={3}
                        placeholder="<p>Enter HTML content...</p>"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Default Tab</label>
                      <select
                        value={fundedData.default_tab}
                        onChange={(e) => setFundedData({ ...fundedData, default_tab: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg"
                      >
                        <option value="inhouse">In-House Projects</option>
                        <option value="externally_aided">Externally Aided Projects</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* In-House Projects Table */}
                <div className="bg-white rounded-lg shadow-sm border p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-bold">Ongoing In-House Projects</h2>
                    <Button onClick={addInHouseProject} size="sm" variant="outline">
                      <Plus className="w-4 h-4 mr-1" />
                      Add Project
                    </Button>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="border p-2">S.No</th>
                          <th className="border p-2">Activity Title</th>
                          <th className="border p-2">Principal Investigator</th>
                          <th className="border p-2">Division</th>
                          <th className="border p-2">Co-PI (Optional)</th>
                          <th className="border p-2">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {fundedData.inhouse_projects.map((project, idx) => (
                          <tr key={idx}>
                            <td className="border p-2 text-center">{project.s_no}</td>
                            <td className="border p-2">
                              <Input
                                value={project.activity_title}
                                onChange={(e) => updateInHouseProject(idx, 'activity_title', e.target.value)}
                                placeholder="Activity title"
                              />
                            </td>
                            <td className="border p-2">
                              <Input
                                value={project.principal_investigator}
                                onChange={(e) => updateInHouseProject(idx, 'principal_investigator', e.target.value)}
                                placeholder="PI name"
                              />
                            </td>
                            <td className="border p-2">
                              <Input
                                value={project.division}
                                onChange={(e) => updateInHouseProject(idx, 'division', e.target.value)}
                                placeholder="Division"
                              />
                            </td>
                            <td className="border p-2">
                              <Input
                                value={project.co_principal_investigator || ''}
                                onChange={(e) => updateInHouseProject(idx, 'co_principal_investigator', e.target.value || null)}
                                placeholder="Co-PI (optional)"
                              />
                            </td>
                            <td className="border p-2 text-center">
                              <button
                                onClick={() => deleteInHouseProject(idx)}
                                className="text-red-600 hover:text-red-800"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Externally Aided Projects Table */}
                <div className="bg-white rounded-lg shadow-sm border p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-bold">Ongoing Externally Aided Projects</h2>
                    <Button onClick={addExternalProject} size="sm" variant="outline">
                      <Plus className="w-4 h-4 mr-1" />
                      Add Project
                    </Button>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="border p-2">S.No</th>
                          <th className="border p-2">Scheme</th>
                          <th className="border p-2">Project Title</th>
                          <th className="border p-2">PI / Nodal Officer</th>
                          <th className="border p-2">Period</th>
                          <th className="border p-2">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {fundedData.externally_aided_projects.map((project, idx) => (
                          <tr key={idx}>
                            <td className="border p-2 text-center">{project.s_no}</td>
                            <td className="border p-2">
                              <Input
                                value={project.scheme}
                                onChange={(e) => updateExternalProject(idx, 'scheme', e.target.value)}
                                placeholder="Scheme"
                              />
                            </td>
                            <td className="border p-2">
                              <Input
                                value={project.project_title}
                                onChange={(e) => updateExternalProject(idx, 'project_title', e.target.value)}
                                placeholder="Project title"
                              />
                            </td>
                            <td className="border p-2">
                              <Input
                                value={project.pi_or_nodal_officer}
                                onChange={(e) => updateExternalProject(idx, 'pi_or_nodal_officer', e.target.value)}
                                placeholder="PI / Nodal officer"
                              />
                            </td>
                            <td className="border p-2">
                              <Input
                                value={project.period}
                                onChange={(e) => updateExternalProject(idx, 'period', e.target.value)}
                                placeholder="e.g. 2022-2025"
                              />
                            </td>
                            <td className="border p-2 text-center">
                              <button
                                onClick={() => deleteExternalProject(idx)}
                                className="text-red-600 hover:text-red-800"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="flex justify-end">
                  <Button
                    onClick={handleSaveFunded}
                    disabled={saving}
                    className="bg-[#0E6D2B] hover:bg-[#0a5a23]"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {saving ? 'Saving...' : 'Save Research Funded'}
                  </Button>
                </div>
              </div>
            )}
          </TabsContent>

          {/* Collaboration Tab */}
          <TabsContent value="collaboration">
            {collabData && (
              <div className="space-y-6">
                {/* Header Fields */}
                <div className="bg-white rounded-lg shadow-sm border p-6">
                  <h2 className="text-xl font-bold mb-4">Page Settings</h2>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Page Title</label>
                      <Input
                        value={collabData.page_title}
                        onChange={(e) => setCollabData({ ...collabData, page_title: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">About Text (HTML)</label>
                      <Textarea
                        value={collabData.about_text}
                        onChange={(e) => setCollabData({ ...collabData, about_text: e.target.value })}
                        rows={4}
                        placeholder="<p>Enter HTML content...</p>"
                      />
                    </div>
                  </div>
                </div>

                {/* Collaborations */}
                <div className="bg-white rounded-lg shadow-sm border p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-bold">Collaboration Partners</h2>
                    <Button onClick={addCollaboration} size="sm" variant="outline">
                      <Plus className="w-4 h-4 mr-1" />
                      Add Partner
                    </Button>
                  </div>

                  <div className="space-y-4">
                    {collabData.collaborations.map((collab, idx) => (
                      <div key={idx} className="border rounded-lg p-4 bg-gray-50">
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="font-semibold text-gray-700">Partner {idx + 1}</h3>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => moveCollaboration(idx, 'up')}
                              disabled={idx === 0}
                              className="p-1 text-gray-600 hover:text-gray-800 disabled:opacity-30"
                            >
                              <MoveUp className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => moveCollaboration(idx, 'down')}
                              disabled={idx === collabData.collaborations.length - 1}
                              className="p-1 text-gray-600 hover:text-gray-800 disabled:opacity-30"
                            >
                              <MoveDown className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => deleteCollaboration(idx)}
                              className="text-red-600 hover:text-red-800"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>

                        <div className="space-y-3">
                          <div>
                            <label className="block text-sm font-medium mb-1">Title</label>
                            <Input
                              value={collab.title}
                              onChange={(e) => updateCollaboration(idx, 'title', e.target.value)}
                              placeholder="e.g., University of Florida, USA"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium mb-1">Description (HTML)</label>
                            <Textarea
                              value={collab.description}
                              onChange={(e) => updateCollaboration(idx, 'description', e.target.value)}
                              rows={3}
                              placeholder="<p>Focus area and collaboration details...</p>"
                            />
                          </div>
                        </div>
                      </div>
                    ))}

                    {collabData.collaborations.length === 0 && (
                      <div className="text-center py-8 text-gray-500">
                        No collaborations added yet. Click "Add Partner" to create your first collaboration.
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex justify-end">
                  <Button
                    onClick={handleSaveCollab}
                    disabled={saving}
                    className="bg-[#0E6D2B] hover:bg-[#0a5a23]"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {saving ? 'Saving...' : 'Save Collaboration'}
                  </Button>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
}
